import { useEffect, useRef } from "react";
import { useAuth } from "@/app/components/auth-context";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Locks the app after 5 minutes of no user interaction.
 * Only runs when authenticated — the lock screen doesn't tick.
 * Tracks mouse, touch, keyboard, and scroll events.
 */
export function useIdleLock() {
  const { authState, lockApp } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (authState !== "authenticated") return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lockApp();
      }, IDLE_TIMEOUT_MS);
    };

    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "mousedown",
      "touchstart",
      "keydown",
      "scroll",
    ];

    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start the timer

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [authState, lockApp]);
}
